import axios from 'axios';
import { BASE_URL, CONTENT_API_PATH } from 'constants/api';

export interface ShortsConvertResponse {
  shorts_content_id: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  batch_job_id?: string | null;
}

export interface ShortsConvertOptions {
  /** BEE-5022 trim 인자. origin video 의 잘라낼 시작 시간(seconds). */
  start_ts?: number | null;
  /** trim 끝 시간(seconds). start_ts 보다 커야 함. */
  end_ts?: number | null;
  /** BEE-4946: 영상 영역 하단 safe area 에 burn-in 할 video_subtitle row id (manual override). */
  subtitle_video_subtitle_id?: number | null;
  /** BEE-4946 (PRD #8/#31): 'off' 보내면 자막 자동 burn 강제 OFF. 미지정 = AUTO. */
  subtitle_burn?: 'off';
  /**
   * ShortsRenderPreset id — 이 건만 다른 스타일로 렌더. 생략하면 시즌의 render_preset,
   * 그것도 없으면 서버 기본값. 없는 id 나 다른 platform 의 id 는 404 (존재 여부를
   * 노출하지 않으려고 둘 다 같은 응답).
   */
  render_preset_id?: number | null;
  /**
   * 상단 에피소드 라벨 문구 (max 255).
   *
   * 3-상태다 — `undefined` 는 필드를 아예 안 보내서 자동값(제목의 `:` 뒤)을 쓰게 하고,
   * 문자열은 그 문구로 렌더하며, **빈 문자열은 라벨을 아예 그리지 않는다.**
   * "입력칸을 비웠다" 를 그대로 `''` 로 넘기면 자동값이 아니라 라벨 삭제가 되므로,
   * 호출부에서 의도를 구분해서 넘겨야 한다.
   */
  label_episode?: string;
  /** 하단 프로그램 라벨 문구 (max 255). 3-상태 규칙은 `label_episode` 와 같다. */
  label_program?: string;
}

export const shortsConvertService = {
  /** content_id 의 원본 영상으로 portrait shorts 변환 트리거 (BEE-4945).
   *  즉시 새 shorts Content + ShortsMeta(original_content=원본) 가 생성되고
   *  status='pending' 으로 응답. 실제 ffmpeg 변환은 후속 Phase.
   *  BEE-5022: opts.start_ts / opts.end_ts 가 주어지면 trim 인자로 함께 전달.
   *  BEE-4946: subtitle_burn='off' 보내면 자막 자동 burn-in 강제 OFF.
   *  ucms-be #952: render_preset_id / label_episode / label_program 으로 렌더 스타일과
   *  상·하단 라벨을 이 건에 한해 덮어쓸 수 있다. */
  async convert(
    contentId: number,
    opts: ShortsConvertOptions = {},
  ): Promise<ShortsConvertResponse> {
    const body: Record<string, number | string> = {};
    if (opts.start_ts != null) body.start_ts = opts.start_ts;
    if (opts.end_ts != null) body.end_ts = opts.end_ts;
    if (opts.subtitle_video_subtitle_id != null) {
      body.subtitle_video_subtitle_id = opts.subtitle_video_subtitle_id;
    }
    if (opts.subtitle_burn === 'off') body.subtitle_burn = 'off';
    if (opts.render_preset_id != null) body.render_preset_id = opts.render_preset_id;
    // `!== undefined` on purpose: '' is a meaningful value here (drop the label),
    // so it must survive into the body while an absent option stays absent.
    if (opts.label_episode !== undefined) body.label_episode = opts.label_episode;
    if (opts.label_program !== undefined) body.label_program = opts.label_program;
    const response = await axios.post<ShortsConvertResponse>(
      `${BASE_URL}${CONTENT_API_PATH}${contentId}/convert-to-shorts/`,
      body,
    );
    return response.data;
  },
};

/**
 * 변환 실패 사유를 사람이 읽을 수 있는 한 줄로.
 *
 * 404 는 render_preset_id 가 없거나 다른 platform 소속일 때 나오는데, 백엔드가 둘을
 * 구분하지 않으므로 "삭제된 것 같다" 로 단정하지 않고 두 경우를 함께 안내한다.
 */
export const formatConvertErrorMessage = (error: unknown): string => {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;

  if (response?.status === 404) {
    return '선택한 렌더 프리셋을 찾을 수 없습니다. 삭제되었거나 다른 platform 의 프리셋입니다. 목록을 새로고침한 뒤 다시 선택해 주세요.';
  }

  const data = response?.data as
    | { detail?: unknown; message?: unknown; errors?: Record<string, unknown> }
    | undefined;

  if (data?.errors && typeof data.errors === 'object') {
    const entries = Object.entries(data.errors);
    if (entries.length > 0) {
      return entries
        .map(([field, messages]) => {
          const text = Array.isArray(messages) ? messages.join(' ') : String(messages);
          return `${field}: ${text}`;
        })
        .join(' / ');
    }
  }

  if (typeof data?.detail === 'string' && data.detail) return data.detail;
  if (typeof data?.message === 'string' && data.message) return data.message;

  const fallback = (error as { message?: string })?.message;
  return fallback || 'unknown';
};
